import React from 'react';

import {
  Box,
  Display,
  DisplayProps,
  Flex,
  Image,
  Paragraph,
  ParagraphProps,
} from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { SectionProps } from '@rover/kibble/patterns/Section/Section.common';
import Accordion from '@rover/react-lib/src/components/lists/Accordion';
import { AccordionItemProps } from '@rover/react-lib/src/components/lists/Accordion/Accordion.common';

import messages from './messages.png';

// Accordion configured for FAQs
type FaqAccordionProps = { faqItems: AccordionItemProps[] };
export const FaqAccordion: React.FC<FaqAccordionProps> = ({ faqItems }) => (
  <Accordion
    data-testid="faq-accordion"
    headingSize="300"
    defaultOpen="first"
    accordionItems={faqItems}
    itemSchemaProps={{
      itemScope: true,
      itemProp: 'mainEntity',
      itemType: 'https://schema.org/Question',
    }}
    bodySchemaProps={{
      itemScope: true,
      itemProp: 'acceptedAnswer',
      itemType: 'https://schema.org/Answer',
    }}
  />
);

type SubComponents = {
  Header: typeof Display;
  Subheader: typeof Paragraph;
};

type FaqSectionProps = {
  faqItems: AccordionItemProps[];
  imageSrc?: string | null;
  imageWidth?: number;
  imageDescription?: string;
} & SectionProps;

// FAQ Section
const FaqSection: React.FC<FaqSectionProps> & SubComponents = ({
  children,
  faqItems,
  imageSrc = messages,
  imageWidth = 242,
  imageDescription = '',
  ...sectionProps
}: FaqSectionProps) => {
  return (
    <Section
      data-testid="faq-section"
      webProps={{ itemScope: true, itemType: 'https://schema.org/FAQPage' }}
      {...sectionProps}
    >
      <Flex flexWrap={['wrap', 'nowrap']} gap="16x" justifyContent="center">
        <Box flex="2 1 80%">
          {children}
          <FaqAccordion faqItems={faqItems} />
        </Box>

        {imageSrc && (
          <Box
            flexBasis={`${imageWidth}px`}
            flexShrink={['1', '1', '0']} // don't allow the image to shrink on desktop
            textAlign="center"
            maxWidth={`${imageWidth}px`}
            display={['none', 'block']}
          >
            <Image src={imageSrc} description={imageDescription} height="auto" width="100%" />
          </Box>
        )}
      </Flex>
    </Section>
  );
};

// header and subheader for the section
FaqSection.Header = ({ children, ...displayProps }: DisplayProps) => (
  <Display size="500" mb="4x" {...displayProps}>
    {children}
  </Display>
);

FaqSection.Subheader = ({ children, ...paragraphProps }: ParagraphProps) => (
  <Paragraph webProps={{ maxWidth: 'none' }} size="200" marginBottom="6x" {...paragraphProps}>
    {children}
  </Paragraph>
);

export default FaqSection;
